import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import Accordion from "@/components/atoms/accordions/RegisterCompanyAccordion";
import styles from "./styles.module.css";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  RegisterCompanyFormInput,
  RegisterCompanyValidationSchema,
} from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import CompanyInformationPanel from "@/components/organisms/RegisterCompanyPanels/CompanyInformationPanel";
import { Container } from "@mantine/core";
import EconomyAndLogisticsPanel from "@/components/organisms/RegisterCompanyPanels/EconomyAndLogisticsPanel";
import SettingsPanel from "@/components/organisms/RegisterCompanyPanels/SettingsPanel";
import TeamPanel from "@/components/organisms/RegisterCompanyPanels/TeamPanel";
import TermsAndConditionsPanel from "@/components/organisms/RegisterCompanyPanels/TermsAndConditionsPanel";
import { useMutation } from "@tanstack/react-query";
import {
  completePartialCompany,
  getOrStartPartialRegistrationByUserId,
  updatePartialCompany,
} from "@/services/RegisterCompany";
import { useAuth } from "@/contexts/AuthContext";
import CompanyTypeEnum from "@/constants/enums/CompanyTypeEnum";
import { ErrorNotificationObj, InfoNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

const RegisterCompanyWizard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const NOTIFY_DEBOUNCE_MS = 10000;
  const notifyTimerRef = useRef<number | null>(null);
  const lastSavedRef = useRef<string>("");

  const {
    handleSubmit,
    control,
    formState: { isValid, errors, dirtyFields },
    setValue,
    getValues,
    reset,
    watch,
    trigger,
  } = useForm<RegisterCompanyFormInput>({
    resolver: zodResolver(RegisterCompanyValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      partialCompanyId: null,
      userId: user?.id as number,
      partialCompany: {
        name: "",
        email: "",
        vatNumber: "",
        leiNumber: "",
        eoriNumber: "",
        phone: "",
        address: [],
        countryCodeIso3: "nor",
        languageId: 578,
        bankAccount: [
          {
            name: "",
            accountNo: "",
            iban: "",
          },
        ],
        defaultCurrency: "NOK",
        companyProvidesTransport: false,
        companyType: CompanyTypeEnum.TRADER,
        timeZone: "Europe/Oslo",
        admin: {
          firstname: user?.firstname || "",
          lastname: user?.lastname || "",
          email: user?.email || "",
          roleType: "ADMIN",
        },
        teamMembers: [],
        membershipType: "BASIC",
        terms: false,
        creditLimit: 0,
        invoiceDaysBeforeDueDate: 0,
        settlementDays: 0,
      },
      updated: "",
      submitted: false,
      errors: [],
    },
  });
  console.log("Render RegisterCompanyWizard", { isValid, errors, dirtyFields });

  const { mutateAsync: submitCompanyRegistration } = useMutation({
    mutationFn: async (data: RegisterCompanyFormInput) => {
      setValue("submitted", true);
      const req = { ...data, submitted: true };
      return await completePartialCompany(req);
    },
    onSuccess: async () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:registerCompany.companyRegistered"))
      );
    },
    onError: async () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:registerCompany.companyRegisteredError")
        )
      );
    },
  });

  const { mutateAsync: mutateGetOrStartPartialCompany } = useMutation({
    mutationFn: async () => {
      return await getOrStartPartialRegistrationByUserId(user?.id as number);
    },
    onSuccess: async (data) => {
      const source = data?.data ?? {};

      if (source.partialCompanyId != null) {
        setValue("partialCompanyId", source.partialCompanyId);
      }
      if (source.submitted === true) {
        notifications.show(
          InfoNotificationObj(
            t("registerCompany:formSubmittedModalTitle"),
            t("registerCompany:formSubmittedModalContent")
          )
        );
      }
      let incomingPartial: any = source.partialCompany ?? {};

      if (incomingPartial && incomingPartial.partialCompany) {
        incomingPartial = incomingPartial.partialCompany;
      }

      const topLevelKeys = ["partialCompanyId", "userId", "updated", "submitted", "errors", "status"];
      for (const k of topLevelKeys) {
        if (incomingPartial && Object.prototype.hasOwnProperty.call(incomingPartial, k)) {
          delete incomingPartial[k];
        }
      }

      const current = getValues();

      const mergedPartialCompany = deepMerge(current.partialCompany ?? {}, incomingPartial ?? {});

      // Ensure bankAccount is always a non-empty array with string fields (server may return null)
      const rawBankAccount = mergedPartialCompany.bankAccount;
      const bankAccount =
        Array.isArray(rawBankAccount) && rawBankAccount.length > 0
          ? rawBankAccount.map((ba: any) => ({
              name: ba?.name ?? "",
              accountNo: ba?.accountNo ?? "",
              iban: ba?.iban ?? "",
            }))
          : [{ name: "", accountNo: "", iban: "" }];

      const adminEmail = user?.email || "";
      const teamMembers = Array.isArray(mergedPartialCompany.teamMembers)
        ? mergedPartialCompany.teamMembers.filter((m: any) => m?.email !== adminEmail)
        : [];

      const merged = deepMerge(current, {
        ...source,
        partialCompany: {
          ...mergedPartialCompany,
          bankAccount,
          teamMembers,
          admin: {
            firstname: user?.firstname || "",
            lastname: user?.lastname || "",
            email: adminEmail,
            roleType: "ADMIN",
          },
          companyProvidesTransport: mergedPartialCompany.companyProvidesTransport ?? true,
          languageId: mergedPartialCompany.languageId ?? 578,
        },
      }) as RegisterCompanyFormInput;
      reset(merged);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:registerCompany.startPartialCompanyError")
        )
      );
    },
  });

  useEffect(() => {
    const startOrGetPartialCompany = async () => {
      await mutateGetOrStartPartialCompany();
    };
    if (user?.id) {
      startOrGetPartialCompany().then();
    }
  }, [user?.id]);

  const { mutateAsync: saveDraftMutate } = useMutation({
    mutationFn: async (req: any) => {
      return await updatePartialCompany(req);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:registerCompany.companyUpdatedError"))
      );
    },
  });

  // debounced notification: 10s after the last successful saveDraft call
  const scheduleDraftSavedNotification = useCallback(() => {
    if (notifyTimerRef.current !== null) {
      clearTimeout(notifyTimerRef.current);
    }
    notifyTimerRef.current = window.setTimeout(() => {
      notifyTimerRef.current = null;
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:registerCompany.companyUpdated"))
      );
    }, NOTIFY_DEBOUNCE_MS);
  }, [t]);

  // called by child panels on blur — saves immediately, schedules the debounced notification
  const saveDraftImmediate = useCallback(async (): Promise<void> => {
    const partial = getValues().partialCompany ?? {};
    const snapshot = JSON.stringify(partial);

    if (snapshot === lastSavedRef.current) {
      return;
    }

    const req = {
      partialCompanyId: getValues().partialCompanyId as number,
      userId: user?.id as number,
      partialCompany: partial,
      updated: getValues().updated,
      submitted: getValues().submitted,
      errors: getValues().errors,
    };

    try {
      await saveDraftMutate(req);
      lastSavedRef.current = snapshot;
      scheduleDraftSavedNotification();
    } catch {
      // error handled by useMutation.onError
    }
  }, [getValues, user?.id, saveDraftMutate, scheduleDraftSavedNotification]);

  // cleanup timers on un mount
  useEffect(() => {
    return () => {
      if (notifyTimerRef.current !== null) {
        clearTimeout(notifyTimerRef.current);
        notifyTimerRef.current = null;
      }
    };
  }, []);

  const p = errors.partialCompany;
  const sectionHasError = {
    companyInfo: !!(p?.name || p?.email || p?.vatNumber || p?.phone || p?.address || p?.countryCodeIso3 || p?.leiNumber || p?.eoriNumber),
    economy: !!(p?.bankAccount || p?.defaultCurrency || p?.creditLimit || p?.invoiceDaysBeforeDueDate || p?.settlementDays),
    settings: !!(p?.companyType || p?.timeZone || p?.languageId || p?.membershipType),
    team: !!(p?.admin || p?.teamMembers),
  };

  const erroneousSections = [
    sectionHasError.companyInfo && t("registerCompany:companyInformationControlLabel"),
    sectionHasError.economy && t("registerCompany:economyAndLogisticsControlLabel"),
    sectionHasError.settings && t("registerCompany:settingsControlLabel"),
    sectionHasError.team && t("registerCompany:teamControlLabel"),
  ].filter(Boolean) as string[];

  const accordionItems = [
    // company information
    {
      value: "company-information",
      label: "registerCompany:companyInformationControlLabel",
      index: 0,
      hasError: sectionHasError.companyInfo,
      panelContent: <CompanyInformationPanel control={control} saveDraft={saveDraftImmediate} errors={errors} />,
    },
    // economy and logistics
    {
      value: "economy-logistics",
      label: "registerCompany:economyAndLogisticsControlLabel",
      index: 1,
      hasError: sectionHasError.economy,
      panelContent: <EconomyAndLogisticsPanel control={control} saveDraft={saveDraftImmediate} />,
    },
    // settings
    {
      value: "settings",
      label: "registerCompany:settingsControlLabel",
      index: 2,
      hasError: sectionHasError.settings,
      panelContent: <SettingsPanel control={control} saveDraft={saveDraftImmediate} />,
    },
    // TEAM
    {
      value: "team",
      label: "registerCompany:teamControlLabel",
      index: 3,
      hasError: sectionHasError.team,
      panelContent: <TeamPanel control={control} saveDraft={saveDraftImmediate} />,
    },
    // Terms and condition
    {
      value: "terms-and-condition",
      label: "registerCompany:termsAndConditionsControlLabel",
      index: 4,
      hasError: false,
      panelContent: <TermsAndConditionsPanel control={control} isValid={isValid} watch={watch} erroneousSections={erroneousSections} />,
    },
  ];

  const onSubmit = async (data: RegisterCompanyFormInput) => {
    await submitCompanyRegistration(data);
  };

  return (
    <React.Fragment>
      <title>{`Reviver - ${t("registerCompany:registerCompany")}`}</title>
      <meta name="description" content={`Reviver - ${t("registerCompany:registerCompany")}`} />
      <main className={styles.registerCompanyRoot}>
        <section className={styles.registerCompanyContent}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Container size={"xl"}>
              <Stack gap="20px" my={20}>
                <Heading order={2} data-testid="register-company-heading">
                  {t("dashboard:welcome") + " "}
                  {user?.firstname ?? ""}
                </Heading>
                <Text data-testid="welcome-text">{t("dashboard:welcomeText")}</Text>
              </Stack>
              <Accordion chevron={<></>} multiple={true} defaultValue={["company-information"]}>
                {accordionItems.map((item) => (
                  <Accordion.Item key={item.value} value={item.value}>
                    <Accordion.Control data-testid={"accordion-" + item.value} icon={<StepIcon index={item.index} hasError={item.hasError} />}>
                      <Text display={"technical"} strong tt={"uppercase"} data-testid={"accordion-label-" + item.value}>
                        {t(item.label)}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>{item.panelContent}</Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Container>
          </form>
        </section>
      </main>
    </React.Fragment>
  );
};

export default RegisterCompanyWizard;

const StepIcon = ({ index, hasError }: { index: number; hasError?: boolean }) => {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" display={"block"}>
      <circle cx="16" cy="16" r="16" fill={hasError ? "#cc0000" : "#000000"} />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize="14"
        fontFamily="HAL Four Grotesk Test"
        fontWeight="400"
      >
        {index + 1}
      </text>
    </svg>
  );
};

function isPlainObject(val: any): val is Record<string, any> {
  return !!val && typeof val === "object" && !Array.isArray(val);
}

function deepMerge<T = any>(target: T, source: Partial<T>): T {
  const out: any = { ...(target as any) };
  for (const key of Object.keys(source)) {
    const s = (source as any)[key];
    const t = (target as any)[key];
    if (Array.isArray(s)) {
      out[key] = s;
    } else if (isPlainObject(s) && isPlainObject(t)) {
      out[key] = deepMerge(t, s);
    } else if (s !== undefined) {
      out[key] = s;
    }
  }
  return out;
}
